import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PageInfo {
  title: string;
  subtitle?: string;
  icon?: string;
  iconUrl?: string; // For custom image icons
}

export interface CurrentMarinaInfo {
  name: string;
  canSwitch: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TopbarStateService {
  private pageInfoSubject = new BehaviorSubject<PageInfo | null>(null);
  private currentMarinaSubject = new BehaviorSubject<CurrentMarinaInfo | null>(null);
  private marinaSelectionHandler: (() => void) | null = null;

  /** Observable for the current page info (title, subtitle, icon) */
  pageInfo$: Observable<PageInfo | null> = this.pageInfoSubject.asObservable();
  /** Observable for the current marina displayed in the topbar */
  currentMarina$: Observable<CurrentMarinaInfo | null> = this.currentMarinaSubject.asObservable();

  constructor() {}

  /**
   * Sets the page info for the topbar
   * @param title The main page title (e.g. 'Clients')
   * @param subtitle Optional sub-text (e.g. 'Manage clients in your marina')
   * @param icon Optional mat-icon name to display next to the title (Lucide icon name)
   * @param iconUrl Optional URL to a custom image icon to display instead of a string icon
   */
  setPageInfo(title: string, subtitle?: string, icon?: string, iconUrl?: string): void {
    // Defer so route child ngOnInit does not update AppComponent bindings mid-check (NG0100).
    queueMicrotask(() => {
      this.pageInfoSubject.next({ title, subtitle, icon, iconUrl });
    });
  }

  /**
   * Clears the current page info, reverting the topbar to its default state
   */
  clearPageInfo(): void {
    this.pageInfoSubject.next(null);
  }

  /**
   * Sets the current marina shown in the topbar.
   * @param name Marina name to display
   * @param canSwitch Whether the marina badge should be clickable
   */
  setCurrentMarina(name: string, canSwitch = false): void {
    this.currentMarinaSubject.next({ name, canSwitch });
  }

  /**
   * Clears the current marina display from topbar.
   */
  clearCurrentMarina(): void {
    this.currentMarinaSubject.next(null);
  }

  /**
   * Registers a callback used when users click the current marina in topbar.
   */
  setMarinaSelectionHandler(handler: (() => void) | null): void {
    this.marinaSelectionHandler = handler;
  }

  /**
   * Triggers marina selection flow from topbar.
   */
  openMarinaSelectionDialog(): void {
    this.marinaSelectionHandler?.();
  }
}
